import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const API_BASE = '/api/v1'

async function apiFetch<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string }
    throw new Error(body.error || 'Request failed')
  }
  return res.json() as Promise<T>
}

export interface Address {
  id: string
  label: string
  type: string
  name: string
  address_line1: string
  address_line2: string | null
  suburb: string
  state: string
  postcode: string
  email: string | null
  phone: string | null
  is_default_sender: boolean
  is_default_recipient: boolean
  created_at: string
  updated_at: string
}

export interface CreateAddressInput {
  label: string
  type?: 'sender' | 'recipient' | 'both'
  name: string
  addressLine1: string
  addressLine2?: string
  suburb: string
  state: string
  postcode: string
  email?: string
  phone?: string
  isDefaultSender?: boolean
  isDefaultRecipient?: boolean
}

export interface LookupResult {
  name: string
  address_line1: string
  suburb: string
  state: string
  postcode: string
  phone: string | null
  website: string | null
}

export function useAddresses(type?: string) {
  const params = new URLSearchParams()
  if (type) params.set('type', type)
  const qs = params.toString() ? `?${params}` : ''

  return useQuery<{ addresses: Address[] }>({
    queryKey: ['addresses', type],
    queryFn: () => apiFetch(`/addresses${qs}`),
  })
}

export function useCreateAddress() {
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, CreateAddressInput>({
    mutationFn: (data) => apiFetch('/addresses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  })
}

export function useUpdateAddress() {
  const qc = useQueryClient()
  return useMutation<{ message: string }, Error, { id: string; data: Partial<CreateAddressInput> }>({
    mutationFn: ({ id, data }) => apiFetch(`/addresses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  })
}

export function useDeleteAddress() {
  const qc = useQueryClient()
  return useMutation<{ message: string }, Error, string>({
    mutationFn: (id) => apiFetch(`/addresses/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  })
}

export function useSetDefaultAddress() {
  const qc = useQueryClient()
  return useMutation<{ message: string }, Error, { id: string; field: 'sender' | 'recipient' }>({
    mutationFn: ({ id, field }) => apiFetch(`/addresses/${id}/default`, {
      method: 'PATCH',
      body: JSON.stringify({ field }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  })
}

export function useAddressLookup() {
  return useMutation<{ results: LookupResult[] }, Error, { q: string; location: string }>({
    mutationFn: ({ q, location }) =>
      apiFetch(`/addresses/lookup?q=${encodeURIComponent(q)}&location=${encodeURIComponent(location)}`),
  })
}
